// src/features/tower/storeReadiness/api.ts


import { readinessFetch } from "../../../auth/readinessFetch";
import type { StoreReadinessPayload, SessionStage, ReadinessTile } from "./types";

export async function getStoreReadiness(params: {
  branch?: string;
  date?: string;
  stage?: SessionStage;
}): Promise<StoreReadinessPayload> {
  const qs = new URLSearchParams();
  if (params.branch) qs.set("branch", params.branch);
  if (params.date) qs.set("date", params.date);
  if (params.stage) qs.set("stage", params.stage);

  const res = await readinessFetch(
    `/.netlify/functions/get-store-readiness?${qs.toString()}`
  );

  return handleResponse<StoreReadinessPayload>(res);
}

function newRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function handleResponse<T>(res: Response): Promise<T> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // no JSON body
  }

  if (!res.ok) {
    const message = (body && body.message) || `Request failed (${res.status})`;
    const err: any = new Error(message);
    err.statusCode = res.status;
    throw err;
  }

  return body as T;
}

// --- Simplified single-click completion (the one the page actually uses) ---
export async function markTaskDone(params: {
  taskEventId?: string;
  rowVersion?: string;
  branchId?: string;
  date?: string;
  stage?: SessionStage;
  taskDefinitionId?: string;
  performedByEmployeeId: string;
  markedByEmployeeId: string;
}): Promise<ReadinessTile> {
  const res = await readinessFetch("/.netlify/functions/mark-readiness-task-done", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

// --- Older, timed/quality-checked flow - kept in the codebase, unused by
//     the page for now, per the decision to simplify to one-click marking ---

export async function startTask(params: {
  branchId: string;
  date: string;
  stage: SessionStage;
  taskDefinitionId: string;
  assignedEmployeeId: string;
}): Promise<ReadinessTile> {
  const res = await readinessFetch("/.netlify/functions/start-readiness-task", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function completeTask(params: {
  taskEventId: string;
  rowVersion: string;
  qualityResult: "PASS" | "COACHING_PASS" | "FAIL" | "NOT_OBSERVED";
  evidenceUrl?: string;
  notes?: string;
}): Promise<ReadinessTile> {
  const res = await readinessFetch("/.netlify/functions/complete-readiness-task", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function verifyTask(params: {
  taskEventId: string;
  rowVersion: string;
  verifierEmployeeId: string;
  decision: "APPROVE" | "REJECT";
  notes?: string;
}): Promise<ReadinessTile> {
  const res = await readinessFetch("/.netlify/functions/verify-readiness-task", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function exceptTask(params: {
  taskEventId?: string;
  rowVersion?: string;
  branchId?: string;
  date?: string;
  stage?: SessionStage;
  taskDefinitionId?: string;
  exceptionCode: string;
  notes: string;
  grantedByEmployeeId: string;
}): Promise<ReadinessTile> {
  const res = await readinessFetch("/.netlify/functions/except-readiness-task", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function recalculateScore(employeeId: string): Promise<{
  snapshotId: string;
  overall: number;
  dataCoverageStatus: string;
}> {
  const res = await readinessFetch("/.netlify/functions/recalculate-readiness-scores", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ employeeId, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function createSopObservation(params: {
  employeeId: string;
  branchId: string;
  sessionId: string;
  sopDefinitionId: string;
  result: "PASS" | "COACHING_PASS" | "FAIL" | "NOT_OBSERVED";
  observerEmployeeId: string;
  evidenceUrl?: string;
  coachingGiven?: boolean;
  notes?: string;
}) {
  const res = await readinessFetch("/.netlify/functions/create-sop-observation", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function createBehaviourObservation(params: {
  employeeId: string;
  branchId: string;
  sessionId: string;
  culturalValue: string;
  observationType: "POSITIVE" | "CORRECTIVE";
  rating: number;
  observableEvidence: string;
  observerEmployeeId: string;
  secondManagerEmployeeId: string;
}) {
  const res = await readinessFetch("/.netlify/functions/create-behaviour-observation", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}

export async function createConductIncident(params: {
  employeeId: string;
  branchId: string;
  sessionId: string;
  incidentType: string;
  location?: string;
  severity: "NON_CRITICAL" | "CRITICAL";
  authorisedUse?: boolean;
  observerEmployeeId: string;
  description: string;
  actionTaken?: string;
}) {
  const res = await readinessFetch("/.netlify/functions/create-conduct-incident", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...params, requestId: newRequestId() }),
  });
  return handleResponse(res);
}
